"use client";

import React, { useState } from "react";
import { notifications } from "@mantine/notifications";
import { Actions } from "Components/Admin/StyleComponent/Actions";
import { Card } from "Components/Admin/StyleComponent/Card";
import { Header } from "Components/Admin/StyleComponent/Header";
import { Table } from "Components/Admin/StyleComponent/Table";
import {
  useGetAllOverallPromotionsQuery,
  useCreateOverallPromotionMutation,
  useUpdateOverallPromotionMutation,
  useDeleteOverallPromotionMutation,
} from "Services/Admin/EcommerceDiscountApiSlice";
import {
  OverallPromotionDTO,
  OverallPromotionResponseDTO,
} from "Types/Admin/EcommerceDiscountType";

const PLANT = process.env.REACT_APP_PLANT ?? "";

const emptyForm: OverallPromotionDTO = {
  discountDesc: "",
  fromPrice: 0,
  isActive: "Y",
  isPriceSlab: "1",
  plant: PLANT,
  promotionType: "",
  promotionValue: 0,
  toPrice: 0,
};

const AdminOverall = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingOverallPromotion, setEditingOverallPromotion] =
    useState<OverallPromotionResponseDTO | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [selectAll, setSelectAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<OverallPromotionDTO>(emptyForm);

  const {
    data: overallPromotions,
    isLoading,
    isError,
    refetch,
  } = useGetAllOverallPromotionsQuery();

  const [createOverallPromotion, { isLoading: isCreating }] =
    useCreateOverallPromotionMutation();
  const [updateOverallPromotion, { isLoading: isUpdating }] =
    useUpdateOverallPromotionMutation();
  const [deleteOverallPromotion] = useDeleteOverallPromotionMutation();

  const formLoading = isCreating || isUpdating || isSubmitting;
  const promotions: OverallPromotionResponseDTO[] = overallPromotions ?? [];

  const handleSelectAll = () => setSelectAll((prev) => !prev);

  const buildFormData = (
    promotion: OverallPromotionResponseDTO,
  ): OverallPromotionDTO => ({
    discountDesc: promotion.discountDesc,
    fromPrice: promotion.fromPrice,
    isActive: promotion.isActive,
    isPriceSlab: String(promotion.isPriceSlab),
    plant: PLANT,
    promotionType: promotion.promotionType,
    promotionValue: promotion.promotionValue,
    toPrice: promotion.toPrice,
  });

  const handleEdit = (promotion: OverallPromotionResponseDTO) => {
    setEditingOverallPromotion(promotion);
    setFormData(buildFormData(promotion));
    setIsViewMode(false);
    setShowForm(true);
  };

  const handleView = (promotion: OverallPromotionResponseDTO) => {
    setEditingOverallPromotion(promotion);
    setFormData(buildFormData(promotion));
    setIsViewMode(true);
    setShowForm(true);
  };

  const handleDelete = async (promotion: OverallPromotionResponseDTO) => {
    try {
      const response: any = await deleteOverallPromotion(promotion.id).unwrap();
      notifications.show({
        message: response?.message,
        color: "red",
      });
      refetch();
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
      ...(name === "isPriceSlab" && value === "0"
        ? { fromPrice: 0, toPrice: 0 }
        : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formLoading) return;

    if (
      formData.isPriceSlab !== "0" &&
      Number(formData.toPrice) <= Number(formData.fromPrice)
    ) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...formData,
      isPriceSlab: String(formData.isPriceSlab),
      ...(formData.isPriceSlab === "0" ? { fromPrice: 0, toPrice: 0 } : {}),
    };

    try {
      if (editingOverallPromotion && !isViewMode) {
        const response: any = await updateOverallPromotion({
          id: editingOverallPromotion.id,
          data: {
            ...editingOverallPromotion,
            ...payload,
          },
        }).unwrap();
        notifications.show({
          message: response?.message,
          color: "green",
        });
      } else if (!isViewMode) {
        const response: any = await createOverallPromotion(payload).unwrap();
        notifications.show({
          message: response?.message,
          color: "green",
        });
      }
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
      throw error;
    } finally {
      setShowForm(false);
      setEditingOverallPromotion(null);
      setIsSubmitting(false);
      refetch();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-red-500">
        Failed to load overall promotions.
      </div>
    );
  }

  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <Header title="Overall Promotion" />
            <button
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              onClick={() => {
                setFormData(emptyForm);
                setEditingOverallPromotion(null);
                setIsViewMode(false);
                setShowForm(true);
              }}
            >
              Add Overall Promotion
            </button>
          </div>

          <Card>
            <Actions hideEdit={false} />
            <Table
              headers={[
                "Description",
                "Promotion Type",
                "Promotion Value",
                "From Price",
                "To Price",
                "Price Slab",
                "Status",
              ]}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
              totalItems={promotions.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              isLoading={isLoading}
            >
              {promotions.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="py-10 text-center text-sm text-gray-400"
                  >
                    No overall promotions found.
                  </td>
                </tr>
              ) : (
                promotions
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((promotion) => (
                    <tr key={promotion.id} className="border-t">
                      <td className="py-3 px-4 text-sm">
                        {promotion.discountDesc}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {promotion.promotionType === "INR"
                          ? "Fixed Amount"
                          : promotion.promotionType === "%"
                            ? "Percentage"
                            : promotion.promotionType}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {promotion.promotionValue}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {promotion.fromPrice}
                      </td>
                      <td className="py-3 px-4 text-sm">{promotion.toPrice}</td>
                      <td className="py-3 px-4 text-sm">
                        {String(promotion.isPriceSlab) === "1" ? "Yes" : "No"}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {promotion.isActive === "Y" ? "Active" : "Inactive"}
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <button
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => handleView(promotion)}
                          title="View"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path
                              fillRule="evenodd"
                              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <button
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => handleEdit(promotion)}
                          title="Edit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(promotion)}
                          title="Delete"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
              )}
            </Table>
          </Card>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingOverallPromotion(null);
            }}
          >
            ← Back
          </button>

          <Header
            title={
              isViewMode
                ? "View Overall Promotion"
                : editingOverallPromotion
                  ? "Edit Overall Promotion"
                  : "Add Overall Promotion"
            }
          />

          <form
            onSubmit={handleSubmit}
            className="font-body tracking-wider space-y-4"
          >
            <div className="bg-white px-6 py-12 rounded-md shadow-md my-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Promotion Description:*
                  </label>
                  <textarea
                    name="discountDesc"
                    value={formData.discountDesc}
                    onChange={handleChange}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    maxLength={200}
                    placeholder="Enter promotion description (max 200 chars)"
                    required
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.discountDesc.length}/200 characters
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Promotion Type:*
                  </label>
                  <select
                    name="promotionType"
                    value={formData.promotionType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    disabled={isViewMode}
                    required
                  >
                    <option value="">Select Promotion Type</option>
                    <option value="%">Percentage</option>
                    <option value="INR">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Promotion Value:*
                  </label>
                  <input
                    type="number"
                    min="0"
                    name="promotionValue"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.promotionValue}
                    onChange={handleChange}
                    onInput={(e) => {
                      const t = e.target as HTMLInputElement;
                      t.value = t.value.replace(/^0+(?=\d)/, "");
                    }}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                    required
                  />
                </div>

                {formData.isPriceSlab !== "0" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      From Price:*
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="fromPrice"
                      onWheel={(e) => e.currentTarget.blur()}
                      value={formData.fromPrice}
                      onChange={handleChange}
                      onInput={(e) => {
                        const t = e.target as HTMLInputElement;
                        t.value = t.value.replace(/^0+(?=\d)/, "");
                      }}
                      readOnly={isViewMode}
                      disabled={isViewMode}
                      className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                      required
                    />
                  </div>
                )}

                {formData.isPriceSlab !== "0" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      To Price:*
                    </label>
                    <input
                      type="number"
                      min={formData.fromPrice}
                      name="toPrice"
                      onWheel={(e) => e.currentTarget.blur()}
                      value={formData.toPrice}
                      onChange={handleChange}
                      onInput={(e) => {
                        const t = e.target as HTMLInputElement;
                        t.value = t.value.replace(/^0+(?=\d)/, "");
                      }}
                      readOnly={isViewMode}
                      disabled={isViewMode}
                      className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                      required
                    />
                  </div>
                )}

                <div className="pt-2 px-2">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Is Price Slab:
                  </label>
                  <label className="px-2">
                    <input
                      type="radio"
                      name="isPriceSlab"
                      value="1"
                      checked={formData.isPriceSlab === "1"}
                      onChange={handleChange}
                      className="mr-1"
                      readOnly={isViewMode}
                      disabled={isViewMode}
                    />
                    Yes
                  </label>
                  <label className="px-2">
                    <input
                      type="radio"
                      name="isPriceSlab"
                      value="0"
                      checked={formData.isPriceSlab === "0"}
                      onChange={handleChange}
                      className="mr-1"
                      readOnly={isViewMode}
                      disabled={isViewMode}
                    />
                    No
                  </label>
                </div>

                <div className="pt-2 px-2">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Status:
                  </label>
                  <label className="px-2">
                    <input
                      type="radio"
                      name="isActive"
                      value="Y"
                      checked={formData.isActive === "Y"}
                      onChange={handleChange}
                      className="mr-1"
                      readOnly={isViewMode}
                      disabled={isViewMode}
                    />
                    Active
                  </label>
                  <label className="px-2">
                    <input
                      type="radio"
                      name="isActive"
                      value="N"
                      checked={formData.isActive === "N"}
                      onChange={handleChange}
                      className="mr-1"
                      readOnly={isViewMode}
                      disabled={isViewMode}
                    />
                    Inactive
                  </label>
                </div>
              </div>

              <hr className="border-gray-300 my-8" />

              <div className="flex items-center justify-start gap-3 mt-4">
                <button
                  className="px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingOverallPromotion(null);
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>

                {!isViewMode && (
                  <button
                    className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
                    type="submit"
                    disabled={formLoading}
                  >
                    {formLoading && (
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    )}
                    {formLoading ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default AdminOverall;
